import { toast } from "@grafit/design-system/components/molecules/toast";
import { useAuth } from "@grafit/frontend-auth/auth-provider";
import { type TFunction, useTranslation } from "@grafit/i18n";
import { useEffect, useRef, useState } from "react";
import { type ReachabilityFailureSource, useBackendReachabilityStore } from "../utils/backend-reachability";

import { loadGlobalStatusBannerBar } from "./global-status-banner-loader";

const BACKEND_REACHABILITY_TOAST_ID = "backend-reachability";

const handleFailureSource: Record<ReachabilityFailureSource, (t: TFunction) => void> = {
  mutation: (t) =>
    toast.error(
      t((r) => r["global.save-error.network"]),
      { id: BACKEND_REACHABILITY_TOAST_ID },
    ),
  query: (t) =>
    toast.error(
      t((r) => r["global.network-error"]),
      { id: BACKEND_REACHABILITY_TOAST_ID },
    ),
};

const useBackendReachabilityToasts = (t: TFunction): void => {
  const reachability = useBackendReachabilityStore();

  useEffect(() => {
    if (!reachability.isBackendReachable) {
      handleFailureSource[reachability.lastFailureSource](t);
      return;
    }
    if (reachability.lastFailureId > 0) {
      toast.success(
        t((r) => r["global.connection-restored"]),
        { id: BACKEND_REACHABILITY_TOAST_ID },
      );
    }
  }, [reachability, t]);
};

export const GlobalStatusBanner = () => {
  const { isBackendReachable } = useBackendReachabilityStore();
  const { isSignedRoleCryptoSupported } = useAuth();
  const { t } = useTranslation();
  useBackendReachabilityToasts(t);
  const isUnsupportedBrowser = isSignedRoleCryptoSupported === false;

  const isVisible = isUnsupportedBrowser || !isBackendReachable;
  const [hasBeenVisible, setHasBeenVisible] = useState(isVisible);
  if (isVisible && !hasBeenVisible) {
    setHasBeenVisible(true);
  }

  const [banner, setBanner] = useState<Awaited<ReturnType<typeof loadGlobalStatusBannerBar>> | null>(null);
  const pendingLoad = useRef<ReturnType<typeof loadGlobalStatusBannerBar> | null>(null);

  useEffect(() => {
    if (banner) {
      return undefined;
    }

    let active = true;
    const load = (): void => {
      pendingLoad.current ??= loadGlobalStatusBannerBar();
      const request = pendingLoad.current;
      void request.then(
        (module) => {
          if (active) {
            setBanner(module);
          }
          return undefined;
        },
        () => {
          if (pendingLoad.current === request) {
            pendingLoad.current = null;
          }
        },
      );
    };
    load();
    window.addEventListener("online", load);
    return () => {
      active = false;
      window.removeEventListener("online", load);
    };
  }, [banner, isVisible]);

  const BannerBar = banner?.GlobalStatusBannerBar;
  return hasBeenVisible && BannerBar && <BannerBar isVisible={isVisible} isUnsupportedBrowser={isUnsupportedBrowser} />;
};
